import type {
  ConfirmHoraCertaQuarantineItem,
  ConfirmHoraCertaQuarantineResponse,
  ExpireHoraCertaQuarantineResponse,
  ExtractedReferenceGroup,
  HoraCertaImportRow,
  HoraCertaOrderImportResponse,
  HoraCertaQuarantineDock,
  HoraCertaQuarantineListQuery,
  HoraCertaQuarantineListResponse,
  HoraCertaPendingThirdOrdersResponse,
  ReferenceEntityType,
  ReferenceMapping,
  ReferenceMappingInput,
  ReferenceMatchResponse,
  ReferenceOption,
} from 'contracts/HoraCertaImport';
import { api } from 'services';

const apiRoot = (api.defaults.baseURL || '').replace(/\/dashboard\/?$/, '');
const importsPath = `${apiRoot}/api/v1/order-imports`;
const quarantinePath = `${importsPath}/hora-certa/quarantine`;
const quarantineListPath = `${apiRoot}/api/v2/order-imports/hora-certa/quarantine`;

const flattenGroupValues = (group: ExtractedReferenceGroup): string[] =>
  group.entries.flatMap((entry) => entry.externalValues);

const matchReferences = async (
  references: ExtractedReferenceGroup[]
): Promise<ReferenceMatchResponse> => {
  const { data } = await api.post<ReferenceMatchResponse>(
    `${importsPath}/reference-matches`,
    {
      source: 'HORA_CERTA',
      references: references.map((group) => ({
        entityType: group.entityType,
        values: flattenGroupValues(group),
      })),
    }
  );
  return data;
};

const findReferenceOptions = async (
  entityType: ReferenceEntityType,
  search: string
): Promise<ReferenceOption[]> => {
  const { data } = await api.get<ReferenceOption[]>(
    `${importsPath}/reference-options/${entityType}`,
    { params: { search } }
  );
  return data;
};

const createMappings = async (
  mappings: ReferenceMappingInput[]
): Promise<ReferenceMapping[]> => {
  const { data } = await api.post<ReferenceMapping[]>(
    `${importsPath}/reference-mappings`,
    { source: 'HORA_CERTA', mappings }
  );
  return data;
};

const updateMapping = async (
  id: number,
  internalId: number
): Promise<ReferenceMapping> => {
  const { data } = await api.put<ReferenceMapping>(
    `${importsPath}/reference-mappings/${id}`,
    { internalId }
  );
  return data;
};

const importOrders = async (
  rows: HoraCertaImportRow[],
  confirmOverwrite = false
): Promise<HoraCertaOrderImportResponse> => {
  const { data } = await api.post<HoraCertaOrderImportResponse>(
    `${importsPath}/hora-certa`,
    confirmOverwrite ? { rows, confirmOverwrite: true } : { rows }
  );
  return data;
};

const findQuarantine = async (
  query: HoraCertaQuarantineListQuery = {}
): Promise<HoraCertaQuarantineListResponse> => {
  const { data } = await api.get<HoraCertaQuarantineListResponse>(
    quarantineListPath,
    { params: query }
  );
  return data;
};

const findPendingThirdOrders = async (
  thirdOrders: string[]
): Promise<string[]> => {
  const { data } = await api.post<HoraCertaPendingThirdOrdersResponse>(
    `${quarantineListPath}/pending-check`,
    { thirdOrders }
  );
  return data.thirdOrders;
};

const findAvailableDates = async (quarantineId: number): Promise<string[]> => {
  const { data } = await api.get<{ dates: string[] }>(
    `${quarantinePath}/${quarantineId}/available-dates`
  );
  return data.dates;
};

const findAvailability = async (
  quarantineId: number,
  date: string,
  cargoTypeId: number
): Promise<HoraCertaQuarantineDock[]> => {
  const { data } = await api.get<HoraCertaQuarantineDock[]>(
    `${quarantinePath}/${quarantineId}/availability`,
    { params: { date, cargoTypeId } }
  );
  return data;
};

const confirmQuarantine = async (
  items: ConfirmHoraCertaQuarantineItem[]
): Promise<ConfirmHoraCertaQuarantineResponse> => {
  const { data } = await api.post<ConfirmHoraCertaQuarantineResponse>(
    `${quarantinePath}/confirm`,
    { items }
  );
  return data;
};

const expirePastQuarantine =
  async (): Promise<ExpireHoraCertaQuarantineResponse> => {
    const { data } = await api.post<ExpireHoraCertaQuarantineResponse>(
      `${quarantinePath}/expire-past`
    );
    return data;
  };

const deleteQuarantineRecord = async (quarantineId: number): Promise<void> => {
  await api.delete(`${quarantinePath}/${quarantineId}`);
};

export function useHoraCertaImport() {
  return {
    matchReferences,
    findReferenceOptions,
    createMappings,
    updateMapping,
    importOrders,
    findQuarantine,
    findPendingThirdOrders,
    findAvailableDates,
    findAvailability,
    confirmQuarantine,
    expirePastQuarantine,
    deleteQuarantineRecord,
  };
}
